// One shared mapping from server error codes to Hebrew messages. Used everywhere
// an error is shown (via ErrorText, or window.alert with messageForError), so no
// screen handles codes on its own. An unmapped code falls back to a readable
// generic message — never the raw code string.
import he from './he';

const MESSAGES = {
  // auth
  missing_credentials: 'יש להזין דוא"ל וסיסמה.',
  invalid_credentials: 'דוא"ל או סיסמה שגויים.',
  unauthorized: 'נדרשת התחברות מחדש.',

  // clients
  name_required: 'יש להזין שם.',
  client_not_found: 'הלקוח לא נמצא.',
  invalid_payment_terms: 'תנאי תשלום לא תקינים.',
  invalid_payment_method: 'אמצעי תשלום לא תקין.',
  invalid_color: 'צבע לא תקין.',
  invalid_theme: 'מראה לא תקין.',

  // leads / pipeline
  invalid_stage: 'שלב לא תקין.',
  invalid_owner: 'בעלים לא תקין.',
  owner_required: 'יש לבחור בעלים לליד.',
  invalid_lead_source: 'מקור ליד לא תקין.',
  client_not_active: 'לא ניתן לפתוח עבודה לליד — יש להפוך אותו ללקוח פעיל תחילה.',
  stage_needs_contact: he.leads.needContact,
  stage_needs_owner: he.leads.needOwner,
  stage_blocked: 'חסר פרט נדרש כדי לקדם את הליד.',
  // The bulk lead delete refused the WHOLE selection — nothing was deleted. Both codes
  // mean the same thing from the partner's side: something in the selection is no
  // longer a lead, and a customer is never deleted from מסך הלידים.
  no_leads_selected: 'לא נבחרו לידים למחיקה.',
  not_a_lead: 'הבחירה כוללת רשומה שאינה ליד — לא נמחק דבר. יש לרענן את המסך ולנסות שוב.',
  lead_has_work: 'הבחירה כוללת לקוח שיש לו עבודות או ריטיינרים — לא נמחק דבר. מחיקת לקוח נעשית מכרטיס הלקוח.',

  // retainers + income source
  retainer_not_found: 'הריטיינר לא נמצא.',
  partner_required: 'יש לבחור שותף/ה.',
  retainer_client_mismatch: 'הריטיינר שייך ללקוח אחר. יש לבחור ריטיינר של אותו לקוח.',
  // These three all describe income_source = 'retainer'. They now say so in those words
  // — "עבודה שההכנסה שלה בריטיינר" — rather than by the label "נושא", which no longer
  // exists in the UI. Naming the income is also the only phrasing that stays true: a job
  // can sit under a retainer and still bill its own margin, so "under a retainer" was
  // never what these errors were actually about.
  topic_requires_retainer: 'עבודה שההכנסה שלה בריטיינר חייבת להשתייך לריטיינר — אין לה הכנסה משלה, והעלויות שלה נזקפות לריטיינר שמחייב עבורה.',
  income_source_locked_has_invoices: 'לא ניתן להעביר את ההכנסה של עבודה לריטיינר לאחר שנרשמו לה חשבוניות. יש לבטל אותן קודם.',
  job_has_no_income: 'לא ניתן לרשום חשבונית על עבודה שההכנסה שלה בריטיינר — ההכנסה נמצאת ברמת הריטיינר. לחיוב נפרד, שנו את מקור ההכנסה של העבודה ל"הכנסה עצמאית".',
  invoice_target_required: 'יש לשייך את החשבונית לריטיינר או לעבודה.',
  invoice_target_ambiguous: 'חשבונית יכולה להשתייך לריטיינר או לעבודה — לא לשניהם.',
  invalid_monthly_amount: 'הסכום החודשי אינו תקין.',
  // One-off projects (migration 067): an engagement billed one fixed amount for its term.
  invalid_fixed_amount: 'מחיר הפרויקט אינו תקין.',
  invalid_billing_mode: 'סוג ההתקשרות אינו תקין.',
  billing_mode_locked_has_invoices: 'לא ניתן לשנות את סוג ההתקשרות לאחר שנרשמו לה חשבוניות.',
  project_has_no_months: 'לפרויקט חד־פעמי אין חודשי חיוב.',
  job_requires_engagement: 'כל עבודה שייכת לריטיינר או לפרויקט חד־פעמי. יש לבחור אחד מהם.',
  // Everything owed is on the books by itself (migration 068).
  extra_description_required: 'יש לכתוב על מה ההכנסה הנוספת.',
  price_below_collected: 'לא ניתן להוריד את מחיר הפרויקט מתחת למה שכבר נגבה עליו.',
  month_has_payments: 'על החודש הזה כבר נרשם תשלום. יש לבטל את התשלום קודם.',
  project_charge_follows_price: 'החיוב של הפרויקט נקבע לפי מחיר הפרויקט — משנים אותו בעריכת הפרויקט.',
  project_charge_is_its_price: 'בפרויקט חד־פעמי החיוב היחיד הוא מחיר הפרויקט. חיוב נוסף נרשם על עבודה שתחתיו.',

  // jobs + shares
  title_required: 'יש להזין כותרת.',
  client_required: 'יש לבחור לקוח.',
  internal_task_links_nothing: 'משימה פנימית אינה משויכת לעבודה או לריטיינר.',
  issuing_partner_required: 'יש לבחור מפיק/ה חשבונית.',
  collector_required: 'יש לבחור מי גבה את התשלום.',
  invalid_revenue_stream: 'סוג הכנסה לא תקין.',
  // The per-invoice label (047/048), not the income unit's revenue stream above.
  invalid_income_category: 'קטגוריית הכנסה לא תקינה.',
  job_not_found: 'העבודה לא נמצאה.',
  invalid_due_date: 'תאריך יעד לא תקין.',
  // A lead's contact history (client_notes).
  invalid_contact_date: 'תאריך השיחה אינו תקין.',
  note_body_required: 'יש לכתוב מה נאמר בשיחה.',
  note_not_found: 'התיעוד לא נמצא.',
  invalid_follow_up_date: 'תאריך החזרה לליד אינו תקין.',
  invalid_start_date: 'תאריך התחלה לא תקין.',
  invalid_end_date: 'תאריך סיום לא תקין.',
  end_before_start: 'תאריך הסיום חייב לחול אחרי תאריך ההתחלה.',
  invalid_agreed_amount: 'שווי מוסכם לא תקין.',
  shares_must_total_100: 'חלוקת הרווח חייבת להסתכם ב-100%.',
  invalid_share_partner: 'שותף לא תקין בחלוקת הרווח.',
  duplicate_share_partner: 'שותף מופיע פעמיים בחלוקת הרווח.',
  invalid_share_pct: 'אחוז לא תקין בחלוקת הרווח.',

  // invoices
  job_required: 'יש לבחור עבודה.',
  amount_required: 'יש להזין סכום.',
  invoice_not_found: 'החשבונית לא נמצאה.',
  invoice_month_exists: 'כבר קיימת חשבונית לחודש זה בריטיינר הזה.',
  invoice_has_payments: 'לא ניתן להסיר חשבונית שקיימים לה תשלומים.',
  invoice_cancelled: 'לא ניתן לערוך חשבונית שבוטלה.',
  invalid_issuing_partner: 'המפיק/ה שנבחר/ה אינו/ה שותף/ה פעיל/ה.',
  invalid_issue_date: 'תאריך החשבונית אינו תקין.',
  invoice_file_not_found: 'הקובץ לא נמצא.',
  invoice_has_no_client: 'לחשבונית אין לקוח, ולכן אין היכן לשמור את הקובץ.',
  client_payment_terms_missing: 'תנאי התשלום של הלקוח אינם קיימים ברשימת תנאי התשלום. עדכנו את הלקוח, או הוסיפו את התנאי במסך ההגדרות.',
  invalid_period_month: 'חודש הריטיינר אינו תקין.',
  amount_below_paid: 'הסכום נמוך מהתשלומים שכבר נרשמו לחשבונית.',

  // payments
  invoice_required: 'יש לבחור חשבונית.',
  invoice_not_collectible: 'לא ניתן לרשום תשלום לחשבונית זו.',
  payment_not_found: 'התשלום לא נמצא — ייתכן שכבר בוטל.',
  exceeds_balance: 'הסכום חורג מהיתרה לתשלום.',

  // dashboard
  invalid_month: 'החודש שנבחר אינו תקין.',

  // expenses
  invalid_category: 'קטגוריה לא תקינה.',
  invalid_context: 'שיוך קטגוריה לא תקין.',
  payer_required: 'יש לבחור מי שילם.',
  invalid_payer: 'המשלם חייב להיות שותף פעיל.',
  payer_duplicate: 'אותו שותף נבחר פעמיים.',

  // client contacts (071)
  invalid_contacts: 'רשימת אנשי הקשר אינה תקינה.',
  contact_needs_detail: 'לכל איש קשר צריך שם, טלפון או דוא"ל.',
  contact_main_multiple: 'אפשר לסמן רק איש קשר ראשי אחד.',
  payer_amount_invalid: 'החלק של כל שותף חייב להיות סכום גדול מאפס.',
  payer_split_mismatch: 'החלקים של השותפים צריכים להסתכם בסכום ההוצאה.',
  // שני השדות נבדקים מול שתי טבלאות שונות, וזה בדיוק ההבדל: המשלם חייב להיות שותף
  // (הוא מה שההתחשבנות קוראת), ומי ששילם מהכיס יכול להיות כל משתמש פעיל — אחרת אי אפשר
  // היה לרשום שהמזכירה קנתה עוגות. ההודעות נפרדות כדי שלא ייקרא כאילו צריך שותף.
  invalid_incurred_by: 'מי ששילם מהכיס חייב להיות משתמש פעיל במערכת.',
  // 403, להבדיל מ-401: המשתמש מחובר, והמסך הזה פשוט אינו שלו. אין טעם לשלוח אותו למסך
  // התחברות — הוא כבר מחובר.
  forbidden: 'אין לך הרשאה למסך הזה.',
  // שתי ההודעות של הרשאות התוכן (מיגרציה 060). כותב תוכן רואה מסך אחד בלבד, ובתוכו רק
  // הריטיינרים והעבודות שהוקצו לו — ולכן דווקא הוא הכי צפוי להיתקל בקיר הרשאות, ודווקא
  // לו "משהו השתבש" חסר תועלת: אין לו מסך אחר לבדוק בו מה קרה. ההודעה אומרת שהעבודה
  // קיימת ופשוט לא הוקצתה לו, כדי שיידע לבקש גישה במקום לנסות שוב.
  job_not_granted: 'העבודה הזו לא הוקצתה לך. יש לפנות למי שמנהל את ההרשאות כדי לקבל אליה גישה.',
  // נאמר לשותף שמנהל הרשאות, לא לכותב: הקצאה קיימת רק למשתמש מסוג "כותב תוכן", כי היא
  // מה שמגדיר מה הוא רואה. לשותף ולמזכירה אין מה להקצות — הם רואים הכול ממילא.
  grants_only_for_limited: 'אפשר להקצות עבודות רק למשתמש מסוג כותב תוכן — שותף ומזכירה רואים ממילא את כל העבודות.',
  expense_not_found: 'ההוצאה לא נמצאה.',
  expense_already_paid: 'ההוצאה כבר סומנה כשולמה.',
  borne_by_required: 'יש לבחור על מי נופלת העלות.',
  invalid_borne_by: 'הבחירה על מי נופלת העלות אינה תקינה.',
  // Raising the charge that covers a marked cost. All three were unmapped until now and
  // reached the partner as the generic "משהו השתבש" — on a dialog whose whole job is to
  // explain what it is about to move.
  expense_not_chargeable: 'ההוצאה אינה מסומנת כמחויבת ללקוח, ולכן אין עליה חיוב להוציא.',
  expense_already_charged: 'ההוצאה כבר חויבה בחשבונית.',
  expense_has_no_job: 'ההוצאה אינה משויכת לעבודה, ולכן אין ממי לגבות עליה.',
  // Raising a charge with its expense (expenses/charge.js).
  conversion_not_acknowledged: 'החיוב הזה הופך את העבודה לעבודה שמחייבת בעצמה. יש לסמן שקראת מה זה משנה לפני השמירה.',
  job_changed_retry: 'העבודה השתנתה בזמן השמירה. נא לפתוח שוב ולשמור מחדש.',
  no_active_revenue_stream: 'אין תחום הכנסה פעיל בהגדרות, ולכן לא ניתן להפוך את העבודה לעבודה שמחייבת. יש להפעיל תחום הכנסה.',
  expense_charge_locked: 'ההוצאה כבר חויבה ללקוח. את סכום החיוב מתקנים בחשבונית במסך הכספים, ועל מי נופלת העלות נעול כל עוד החיוב קיים.',
  expense_has_live_charge: 'יש על ההוצאה חיוב פעיל. יש לבטל את החיוב במסך הכספים לפני מחיקת ההוצאה.',
  invalid_charge_amount: 'סכום החיוב חייב להיות מספר חיובי.',
  collector_locked_after_payment: 'כבר נרשם תשלום על החשבונית, ולכן מי שגבה אינו ניתן לשינוי מכאן. טעות מתקנים בעריכת החשבונית.',

  // settings (editable lists)
  unknown_list: 'רשימה לא מוכרת.',
  label_required: 'יש להזין תווית.',
  value_not_found: 'הערך לא נמצא.',
  value_in_use: 'לא ניתן למחוק ערך שנמצא בשימוש — יש להשבית אותו במקום.',
  due_days_required: 'יש להזין מספר ימים.',
  invalid_due_days: 'מספר ימים לא תקין.',
  invalid_due_basis: 'יש לבחור מאיפה סופרים את הימים.',

  // tasks
  invalid_status: 'סטטוס לא תקין.',
  invalid_urgency: 'רמת דחיפות לא תקינה.',
  task_not_found: 'המשימה לא נמצאה.',
  // משימה אוטומטית אינה רשומה שמישהו יצר אלא המצב הנוכחי של התראה — היא נוצרת מחדש
  // בטעינת הלוח הבאה. היא נסגרת מעצמה כשהסיבה שלה נפתרת.
  auto_task_already_open: 'כבר יש משימה אוטומטית פתוחה על אותו עניין — אפשר להמשיך בה.',
  auto_task_not_deletable: 'אי אפשר למחוק משימה אוטומטית — היא תיווצר מחדש בטעינה הבאה. היא תיסגר מעצמה כשתירשם החשבונית או כשהריטיינר יחודש או ייסגר.',

  // a task's tracking list (078)
  list_not_found: 'למשימה הזו אין רשימה.',
  list_locked_task_done: 'המשימה בוצעה, והרשימה שבה לקריאה בלבד. כדי לשנות — יש להחזיר את המשימה לפתוחה.',
  auto_task_no_list: 'אי אפשר להוסיף רשימה למשימה אוטומטית.',
  task_has_list: 'למשימה הזו כבר יש רשימה.',
  list_states_required: 'צריך לפחות מצב אחד (ועד 12).',
  list_too_many_columns: 'עד 12 עמודות.',
  invalid_list_tone: 'צבע לא תקין.',
  invalid_list_column_kind: 'סוג עמודה לא תקין.',
  list_state_label_required: 'לכל מצב צריך שם (עד 40 תווים).',
  list_column_label_required: 'לכל עמודה צריך שם (עד 40 תווים).',
  list_state_duplicate: 'שני מצבים באותו שם.',
  list_column_duplicate: 'שתי עמודות באותו שם.',
  invalid_list_state: 'המצב לא שייך לרשימה הזו.',
  invalid_list_column: 'העמודה לא שייכת לרשימה הזו.',
  invalid_list_cells: 'ערכים לא תקינים.',
  invalid_list_group: 'אפשר לקבץ לפי מצב או לפי עמודת טקסט של הרשימה.',
  invalid_list_sort: 'סדר לא תקין.',
  list_value_too_long: 'ערך ארוך מדי (עד 500 תווים).',
  list_note_too_long: 'הערה ארוכה מדי (עד 500 תווים).',
  list_item_name_required: 'יש להזין שם.',
  list_item_name_too_long: 'שם ארוך מדי (עד 160 תווים).',
  list_batch_too_large: 'אפשר להוסיף עד 500 שורות בבת אחת.',
  list_items_required: 'לא נבחרו שורות.',
  list_item_not_found: 'השורה לא נמצאה — אולי נמחקה. יש לרענן.',
  list_state_in_use: 'יש שורות במצב הזה. יש להעביר אותן למצב אחר לפני שמסירים אותו.',

  // publications + file storage
  publication_not_found: 'הפרסום לא נמצא.',
  outlets_required: 'יש לבחור לפחות עיתון אחד שבו הפריט פורסם.',
  invalid_outlet: 'עיתון לא תקין.',
  outlet_not_on_publication: 'העיתון אינו מופיע ברשימת העיתונים של הפרסום.',
  file_outlet_required: 'כל קובץ שייך לעיתון מסוים — יש לבחור לאיזה עיתון הקטע שייך.',
  // The server names the outlets in `details`; this is the fallback wording.
  outlet_has_clipping: 'לא ניתן להסיר עיתון שיש לו קטע עיתונות מצורף — יש להסיר את הקובץ קודם.',
  invalid_date: 'תאריך לא תקין.',
  invalid_job: 'עבודה לא תקינה.',
  scope_required: 'חסר שיוך לעבודה או ללקוח.',
  file_required: 'יש לבחור קובץ.',
  file_not_found: 'הקובץ לא נמצא.',
  file_too_large: 'הקובץ גדול מדי — מותר עד 20MB.',
  invalid_upload: 'העלאת הקובץ נכשלה.',
  unsupported_file_type: 'סוג קובץ לא נתמך. ניתן להעלות מסמך Word או PDF, ותמונות JPG, PNG או WEBP.',
  // The server boots without storage credentials on purpose — only file actions fail.
  storage_not_configured: 'אחסון הקבצים אינו מוגדר. יש להשלים את הגדרות R2 בשרת.',
  // The delete stopped BEFORE touching the database: the objects could not be cleared
  // from storage, and deleting the rows first would strand them there permanently —
  // the rows hold the only record of where those files are. Nothing was deleted.
  storage_cleanup_failed: 'מחיקת הקבצים מהאחסון נכשלה, ולכן שום דבר לא נמחק. יש לנסות שוב.',

  // a signed agreement — a retainer's (the client's contract) or a job's (a supplier's).
  // The message names neither, because the screen it appears on already has.
  agreement_not_found: 'ההסכם לא נמצא.',

  // general business documents (the office filing cabinet)
  document_not_found: 'המסמך לא נמצא.',
  document_category_required: 'יש לבחור קטגוריה למסמך.',
  invalid_document_category: 'קטגוריית מסמך לא תקינה.',

  // the calendar (meetings)
  meeting_not_found: 'הפגישה לא נמצאה.',
  invalid_meeting_kind: 'יש לבחור סוג פגישה — עם לקוח, פנימית או עם גורם חיצוני.',
  client_required_for_client_meeting: 'יש לבחור לקוח לפגישה.',
  partner_required_for_internal_meeting: 'יש לבחור עם מי מהשותפים הפגישה.',
  same_partner: 'פגישה פנימית היא בין שני שותפים שונים.',
  invalid_partner: 'השותף שנבחר אינו פעיל.',
  invalid_meeting_date: 'תאריך הפגישה אינו תקין.',
  invalid_start_time: 'שעת ההתחלה אינה תקינה. שעות הפעילות הן 08:00–24:00, בכל חצי שעה עגולה.',
  invalid_duration: 'משך הפגישה אינו תקין.',
  outside_working_hours: 'הפגישה חורגת משעות הפעילות — היום מסתיים בחצות. אפשר לקצר אותה או להקדים את השעה.',
  // The one a partner will actually meet: the grid said free, and between reading it and
  // saving, the other browser took the slot. It says what to do rather than only what
  // went wrong.
  slot_taken: 'השעה הזו כבר תפוסה ביומן. יש לרענן את המסך ולבחור שעה אחרת.',
  job_client_mismatch: 'העבודה שייכת ללקוח אחר. יש לבחור עבודה של אותו לקוח.',
  summary_required: 'יש לכתוב סיכום לפגישה.',
  meeting_cancelled: 'הפגישה בוטלה — אי אפשר לסכם פגישה שלא התקיימה.',
  invalid_meeting_status: 'סטטוס פגישה לא תקין.',
  // a meeting with someone outside the system (077)
  external_name_required: 'יש לכתוב עם מי הפגישה.',
  external_name_too_long: 'השם ארוך מדי — עד 160 תווים.',
  external_details_too_long: 'הפרטים ארוכים מדי — עד 200 תווים.',
  meeting_not_external: 'הפגישה כבר משויכת ללקוח.',
  search_too_short: 'יש להקליד לפחות שני תווים לחיפוש.',

  // --- פתקים (לוח הפתקים) -------------------------------------------------------------
  // ⚠ קודים נפרדים מאלה של תיעוד שיחה עם ליד (note_body_required / note_not_found), ולא
  // שימוש חוזר בהם: שם מדובר במה נאמר בשיחה, כאן בפתק על לוח משותף. ניסוח אחד שמשרת את
  // שניהם היה מדויק באף אחד מהם.
  note_title_required: 'יש להזין כותרת לפתק.',
  note_title_too_long: 'הכותרת ארוכה מדי — עד 160 תווים.',
  note_text_required: 'יש לכתוב את תוכן הפתק.',
  user_note_not_found: 'הפתק לא נמצא.',
  invalid_note_recipient: 'אי אפשר להשאיר פתק למשתמש הזה.',
  // ההודעה מסבירה את הכלל, לא רק שהפעולה נחסמה: פתק הוא דבר שמישהו אמר, ומי שקיבל אותו
  // אינו מי שכתב אותו.
  not_note_author: 'אפשר לערוך או למחוק רק פתקים שכתבתם.',

  // database-level constraint violations (translated in the error middleware)
  duplicate_entry: 'הרשומה כבר קיימת — קיים ערך זהה.',

  // --- ניהול משתמשים -----------------------------------------------------------------
  user_not_found: 'המשתמש לא נמצא.',
  invalid_email: 'כתובת אימייל לא תקינה.',
  invalid_role: 'תפקיד לא תקין.',
  password_too_short: 'הסיסמה חייבת להכיל לפחות 8 תווים.',
  // שני השומרים הקריטיים. ההודעות מסבירות מה קורה אם הפעולה תתבצע, לא רק שהיא נחסמה —
  // "אי אפשר" בלי סיבה הוא בדיוק מה שגורם לאנשים לנסות לעקוף.
  last_active_partner: 'זהו השותף הפעיל האחרון. השבתה, מחיקה או שינוי תפקיד שלו היו משאירים את המערכת בלי אף מנהל, ואין דרך לתקן זאת מאף מסך.',
  partner_holds_money: 'לשותף הזה יש רישומים כספיים — חשבוניות, הוצאות ששילם או אחוזי רווח. הוצאתו מרשימת השותפים הפעילים שוברת את חישוב ההתחשבנות, ומסך ההתחשבנות והדשבורד יפסיקו לעבוד עד להחזרתו.',
  cannot_promote_to_partner: 'אי אפשר להפוך משתמש לשותף מתוך המסך הזה. שותף הוא זהות עסקית עם חלוקת רווחים, והגדרתו נעשית בנפרד.',
  cannot_demote_partner: 'אי אפשר להוריד שותף מתפקידו מתוך המסך הזה — זהותו הכספית קשורה לחשבוניות ולחלוקת הרווחים.',
  invalid_reference: 'הפניה לנתון שאינו קיים.',

  // THE THREE FAILURES THAT ARE NOT A DOMAIN'S FAULT, and which until now were the only
  // ones that reached the screen as the generic "אירעה שגיאה" — precisely the cases where
  // the generic message is least useful, because nothing on the form is wrong and there is
  // nothing for the user to correct.
  //
  // The first two are the error middleware's own literals (middlewares/error.js), not a
  // `new HttpError(...)` anywhere, which is why `npm run check:errors` never reported them
  // missing. That scanner now reads the middleware too.
  not_found: 'הכתובת המבוקשת לא נמצאה בשרת. אם זה קרה אחרי עדכון, יש לרענן את הדף.',
  internal_error: 'שגיאה בשרת. הפעולה לא בוצעה — כדאי לנסות שוב, ואם זה חוזר יש לבדוק את יומן השרת.',
  // Raised by api/http.js when the response body is not JSON at all: in practice the API
  // is down or mid-restart and the dev proxy answered instead of it. It says so, rather
  // than blaming the form.
  server_unreachable: 'השרת אינו מגיב כרגע — ייתכן שהוא עולה מחדש. יש להמתין רגע ולנסות שוב.',
};

// Resolve any thrown error to a Hebrew message. Unknown/technical errors (e.g. a
// 500 or a network failure) fall back to the generic readable message.
//
// Some errors carry `details` with the specifics the sentence needs — which outlet
// still holds a clipping, for instance, or which row in a selection was refused. When
// present they are appended, so the user is told WHICH one rather than just that
// something is in the way.
export const messageForError = (error) => {
  const code = error && error.message;
  const base = (code && MESSAGES[code]) || he.common.error;
  const details = (error && error.details) || {};
  const named = details.outlets || details.names;
  return named && named.length ? `${base} (${named.join(', ')})` : base;
};

export default MESSAGES;
